import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { enrollmentService, teacherProfileService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { getApiErrorMessage } from '../utils/apiErrors';
import { useConfirm } from '../contexts/ConfirmContext';
import { Card, Badge, Button, Modal, Input, Select, Textarea, Field, EmptyState, Spinner } from '../components/ui';

const EMPTY_FORM = {
  method_type: 'bank_transfer', label: '', account_holder_name: '',
  account_number: '', bank_name: '', instructions: '', is_active: true,
};

const METHOD_ICONS = { bank_transfer: '🏦', mobile_payment: '📱', cash: '💵', other: '💳' };

function PaymentAccounts() {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    teacherProfileService.getMine()
      .then((res) => setPlan(res.data.current_plan || null))
      .catch(() => setPlan(null));
  }, []);

  const fetchAccounts = () => {
    setLoading(true);
    enrollmentService.getPaymentAccounts()
      .then((res) => {
        const data = res.data;
        setAccounts(Array.isArray(data) ? data : data.results || []);
      })
      .catch(() => addNotification(t('Failed to load payment accounts'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchAccounts, []); // eslint-disable-line react-hooks/exhaustive-deps

  const mine = accounts.filter((a) => !a.is_platform_default);
  const defaults = accounts.filter((a) => a.is_platform_default);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a }); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const res = await enrollmentService.updatePaymentAccount(editing.id, form);
        setAccounts((prev) => prev.map((a) => (a.id === editing.id ? res.data : a)));
      } else {
        const res = await enrollmentService.createPaymentAccount(form);
        setAccounts((prev) => [...prev, res.data]);
      }
      addNotification(t('Payment account saved'), 'success');
      setModalOpen(false);
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save payment account'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!(await confirm(t('Delete payment account "{{label}}"?', { label: a.label })))) return;
    try {
      await enrollmentService.deletePaymentAccount(a.id);
      setAccounts((prev) => prev.filter((x) => x.id !== a.id));
      addNotification(t('Payment account deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete payment account'), 'error');
    }
  };

  const methodLabels = {
    bank_transfer: t('Bank Transfer'), mobile_payment: t('Mobile Payment'), cash: t('Cash'), other: t('Other'),
  };

  const renderCard = (a, editable) => (
    <Card key={a.id}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-semibold text-gray-900">{METHOD_ICONS[a.method_type]} {a.label}</p>
          <p className="text-xs text-gray-500">{methodLabels[a.method_type] || a.method_type}</p>
        </div>
        <Badge tone={a.is_active ? 'green' : 'gray'}>{a.is_active ? t('Active') : t('Inactive')}</Badge>
      </div>
      <div className="mt-3 space-y-1 text-sm text-gray-700">
        {a.account_holder_name && <p><span className="text-gray-500">{t('Holder')}:</span> {a.account_holder_name}</p>}
        {a.account_number && <p><span className="text-gray-500">{t('Account #')}:</span> {a.account_number}</p>}
        {a.bank_name && <p><span className="text-gray-500">{t('Bank')}:</span> {a.bank_name}</p>}
        {a.instructions && <p className="text-gray-500 italic mt-1">{a.instructions}</p>}
      </div>
      {editable && (
        <div className="flex gap-2 mt-4">
          <Button size="sm" variant="secondary" onClick={() => openEdit(a)}>{t('Edit')}</Button>
          <Button size="sm" variant="danger" onClick={() => remove(a)}>{t('Delete')}</Button>
        </div>
      )}
    </Card>
  );

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Payment Accounts')} subtitle={t('Where students send payment for your courses')} />
        <main className="flex-1 overflow-y-auto p-6">
          {plan && (
            <Card className="mb-6 bg-gradient-to-r from-primary-50 to-purple-50 border-primary-100">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-xs font-medium text-primary-700 uppercase tracking-wide">{t('My Plan')}</p>
                  <p className="font-display font-bold text-lg text-gray-900 mt-1">{plan.plan_name}</p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {Number(plan.price) === 0
                      ? t('Full access to all features, free until {{date}}.', { date: plan.end_date ? new Date(plan.end_date).toLocaleDateString() : t('further notice') })
                      : t('{{price}}/{{cycle}}, renews {{date}}.', { price: `${plan.price} ${plan.currency}`, cycle: plan.billing_cycle, date: plan.end_date ? new Date(plan.end_date).toLocaleDateString() : '—' })}
                  </p>
                </div>
                <Badge tone={plan.is_current ? 'green' : 'gray'}>{plan.is_current ? t('Active') : t('Expired')}</Badge>
              </div>
            </Card>
          )}

          <div className="flex justify-end mb-4">
            <Button onClick={openCreate}>+ {t('New Payment Account')}</Button>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : (
            <>
              {mine.length === 0 ? (
                <Card><EmptyState title={t('No payment accounts yet')} description={t('Add one so students know where to send payment, then attach it to a course from Edit Course.')} action={<Button onClick={openCreate}>+ {t('New Payment Account')}</Button>} /></Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {mine.map((a) => renderCard(a, true))}
                </div>
              )}

              {defaults.length > 0 && (
                <>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('Platform default accounts (fallback if you attach none of your own)')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {defaults.map((a) => renderCard(a, false))}
                  </div>
                </>
              )}
            </>
          )}
        </main>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('Edit Payment Account') : t('New Payment Account')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Method')} required>
            <Select value={form.method_type} onChange={(e) => setForm({ ...form, method_type: e.target.value })}>
              <option value="bank_transfer">{t('Bank Transfer')}</option>
              <option value="mobile_payment">{t('Mobile Payment')}</option>
              <option value="cash">{t('Cash')}</option>
              <option value="other">{t('Other')}</option>
            </Select>
          </Field>
          <Field label={t('Label')} required hint={t('e.g. "Main CCP Account", "BaridiMob"')}>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Field>
          <Field label={t('Account Holder Name')}>
            <Input value={form.account_holder_name} onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })} />
          </Field>
          <Field label={t('Account Number')} hint={t('IBAN / RIP / CCP / phone number')}>
            <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
          </Field>
          <Field label={t('Bank Name')}>
            <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
          </Field>
          <Field label={t('Instructions')}>
            <Textarea rows={3} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder={t('e.g. Include your full name as payment reference.')} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {t('Active (visible to students)')}
          </label>
        </div>
      </Modal>
    </div>
  );
}

export default PaymentAccounts;
